import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

const reviews = [
  {
    name: "Sarah Johnson",
    title: "Marketing Manager",
    text: "TailorWiz helped me land my dream job! My match score went from 65% to 94% in seconds. The AI perfectly aligned my experience with the job requirements."
  },
  {
    name: "Michael Chen",
    title: "Software Engineer",
    text: "As a developer, I was skeptical about AI resume tools. But TailorWiz is incredible - it understood my technical skills and matched them perfectly to job postings."
  },
  {
    name: "Emily Rodriguez",
    title: "HR Director",
    text: "I've been in HR for 10 years and can spot a good resume instantly. TailorWiz creates resumes that stand out from the pile and get noticed by hiring managers."
  },
  {
    name: "David Thompson",
    title: "Sales Executive",
    text: "Used TailorWiz for 5 different applications and got interviews for 4 of them! The keyword optimization and match scores are game-changing."
  },
  {
    name: "Lisa Wang",
    title: "Data Analyst",
    text: "The quantification feature is amazing. It took my generic bullet points and turned them into powerful, metrics-driven achievements that employers love."
  },
  {
    name: "James Mitchell",
    title: "Project Manager",
    text: "Finally, a resume tool that actually works! My applications went from being ignored to getting callbacks within days. Worth every penny."
  },
  {
    name: "Amanda Foster",
    title: "UX Designer",
    text: "TailorWiz understood my creative background and tailored my resume perfectly for tech companies. The ATS optimization is fantastic."
  },
  {
    name: "Robert Kim",
    title: "Financial Analyst",
    text: "The speed is unbelievable - 60 seconds to completely tailor my resume! And the quality is better than what I could do manually in hours."
  },
  {
    name: "Jennifer Adams",
    title: "Nurse Practitioner",
    text: "Switching careers was daunting, but TailorWiz helped highlight my transferable skills beautifully. Got my first healthcare interview within a week!"
  },
  {
    name: "Christopher Lee",
    title: "Operations Manager",
    text: "The AI catches details I would never think of. My resume now speaks the exact language that employers in my industry are looking for."
  },
  {
    name: "Rachel Green",
    title: "Content Strategist",
    text: "TailorWiz made my resume more compelling and targeted. The before-and-after difference was night and day. Highly recommend!"
  },
  {
    name: "Kevin Martinez",
    title: "Cybersecurity Specialist",
    text: "The technical accuracy is impressive. TailorWiz knew exactly which security certifications and skills to emphasize for each role I applied to."
  },
  {
    name: "Ashley Brown",
    title: "Account Executive",
    text: "I was stuck in application limbo for months. TailorWiz changed everything - now I'm getting responses and moving forward in interview processes."
  },
  {
    name: "Tyler Wilson",
    title: "Business Analyst",
    text: "The match score feature helps me focus on the right opportunities. No more wasting time on jobs where I'm not a good fit."
  },
  {
    name: "Nicole Davis",
    title: "Product Manager",
    text: "TailorWiz understands product management better than most recruiters! My resume now showcases my impact in ways that resonate with hiring managers."
  },
  {
    name: "Brandon Taylor",
    title: "Digital Marketing Specialist",
    text: "The keyword optimization is spot-on. My resume now passes ATS systems that used to filter me out automatically."
  },
  {
    name: "Stephanie Clark",
    title: "Human Resources Manager",
    text: "As an HR professional, I know what recruiters look for. TailorWiz creates resumes that check all the right boxes and tell compelling stories."
  },
  {
    name: "Jonathan Wright",
    title: "Software Architect",
    text: "The technical depth is remarkable. TailorWiz positioned my architecture experience perfectly for senior-level roles I was targeting."
  },
  {
    name: "Megan Turner",
    title: "Graphic Designer",
    text: "Even for creative roles, TailorWiz improved my resume significantly. It highlighted my design process and client impact in ways I hadn't considered."
  },
  {
    name: "Andrew Phillips",
    title: "Construction Manager",
    text: "TailorWiz works across all industries! It tailored my construction experience perfectly for facility management roles I was pursuing."
  },
  {
    name: "Samantha Moore",
    title: "Teacher Transitioning to Corporate",
    text: "Career change was intimidating, but TailorWiz showed me how my teaching skills translate to corporate training roles. Got my first corporate interview!"
  },
  {
    name: "Daniel Garcia",
    title: "Supply Chain Analyst",
    text: "The AI understands supply chain complexities and positioned my logistics experience perfectly. My applications started getting noticed immediately."
  },
  {
    name: "Lauren White",
    title: "Social Media Manager",
    text: "TailorWiz helped me transition from traditional marketing to social media by highlighting my relevant digital experience. Amazing results!"
  },
  {
    name: "Marcus Johnson",
    title: "Quality Assurance Engineer",
    text: "The attention to detail is incredible. TailorWiz emphasized my testing methodologies and quality metrics in exactly the right way."
  },
  {
    name: "Olivia Anderson",
    title: "Executive Assistant",
    text: "TailorWiz elevated my resume from basic admin work to strategic business support. Now I'm interviewing for executive-level positions!"
  },
  {
    name: "Carlos Rivera",
    title: "Data Scientist",
    text: "The AI understood my complex analytics background perfectly. My resume now showcases my machine learning projects in a way that hiring managers actually understand."
  },
  {
    name: "Patricia Williams",
    title: "Retail Manager to Corporate Trainer",
    text: "Career pivot was scary, but TailorWiz showed me how my people management skills translate to corporate training. Got my first training role within 3 weeks!"
  }
];

export default function Reviews() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">TailorWiz Reviews</span>
          </div>
          <div></div> {/* Spacer for centering */}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-7xl">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-foreground mb-6">
            What Our Users Say
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Join thousands of job seekers who have transformed their careers with TailorWiz. 
            Read authentic reviews from professionals across all industries who have successfully 
            landed interviews and jobs using our AI-powered resume tailoring platform.
          </p>
          
          {/* Stats */}
          <div className="flex justify-center items-center gap-8 mb-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">5/5</div>
              <div className="text-sm text-muted-foreground">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">1,200+</div>
              <div className="text-sm text-muted-foreground">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">99%</div>
              <div className="text-sm text-muted-foreground">Success Rate</div>
            </div>
          </div>

          {/* Star Rating Display */}
          <div className="flex justify-center items-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
            ))}
          </div>
          <p className="text-muted-foreground">Based on 1,200+ verified reviews</p>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.map((review, index) => (
            <Card key={index} className="border-border hover:shadow-lg transition-all duration-300 bg-card">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-muted-foreground italic leading-relaxed">
                  "{review.text}"
                </blockquote>
                <div className="pt-3 border-t border-border">
                  <p className="font-semibold text-foreground">{review.name}</p>
                  <p className="text-sm text-muted-foreground">{review.title}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <Card className="bg-gradient-to-r from-primary/10 to-primary-dark/10 border-primary/20">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Ready to Join Our Success Stories?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Start tailoring your resume today and become our next success story. 
                Get the job you deserve with TailorWiz.
              </p>
              <Button 
                size="lg" 
                onClick={() => navigate('/pricing')}
                className="text-lg px-8 py-6 h-auto"
              >
                Try It Out FREE Now
                <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card/30 py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">
            © 2025 TailorWiz. All rights reserved. Helping professionals land their dream jobs.
          </p>
        </div>
      </footer>
    </div>
  );
}